<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import CannedAttachmentsPreview from './CannedAttachmentsPreview.vue';
import { MAXIMUM_FILE_UPLOAD_SIZE } from 'shared/constants/messages';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

const props = defineProps({
  id: { type: Number, default: null },
  edcontent: { type: String, default: '' },
  edshortCode: { type: String, default: '' },
  edattachments: { type: Array, default: () => [] },
  onClose: { type: Function, default: () => {} },
});

const { t } = useI18n();
const store = useStore();

const show = ref(true);
const shortCode = ref(props.edshortCode);
const content = ref(props.edcontent);
const attachments = ref(
  props.edattachments.map(a => ({
    signed_id: a.signed_id,
    thumb: a.thumb_url,
    resource: {
      name: a.blob?.filename,
      size: a.blob?.byte_size,
      type: a.file_type,
    },
  }))
);
const editCanned = ref({ showLoading: false });

const rules = {
  shortCode: { required, minLength: minLength(2) },
  content: { required },
};
const v$ = useVuelidate(rules, { shortCode, content });

const pageTitle = computed(
  () => `${t('CANNED_MGMT.EDIT.TITLE')} - ${props.edshortCode}`
);

function resetForm() {
  shortCode.value = '';
  content.value = '';
  attachments.value = [];
  v$.value.$reset();
}

function handleFileUpload(event) {
  const files = Array.from(event.target.files);
  files.forEach(file => {
    if (!checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
      useAlert(
        t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: MAXIMUM_FILE_UPLOAD_SIZE,
        })
      );
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        attachments.value.push({ resource: file, thumb: reader.result });
      };
    }
  });
  event.target.value = null;
}

function removeAttachment(updated) {
  attachments.value = updated;
}

async function editCannedResponse() {
  editCanned.value.showLoading = true;
  try {
    await store.dispatch('updateCannedResponse', {
      id: props.id,
      short_code: shortCode.value,
      content: content.value,
      attachments: attachments.value.map(a => a.signed_id || a.resource),
    });
    useAlert(t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    resetForm();
    setTimeout(() => {
      props.onClose();
    }, 10);
  } catch (error) {
    const message = error?.message || t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
    useAlert(message);
  } finally {
    editCanned.value.showLoading = false;
  }
}
</script>

<template>
  <Modal v-model:show="show" @close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="w-full mt-4">
          <input type="file" multiple class="mb-2" @change="handleFileUpload" />
          <CannedAttachmentsPreview
            v-if="attachments.length"
            :attachments="attachments"
            @remove-attachment="removeAttachment"
          />
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}

:deep(.ProseMirror-woot-style p) {
  @apply text-base;
}
</style>
